// WelcomeFrame: the welcome page's first screen in a view other than the
// flow board (welcome/views.js): the view, and under it the line saying
// which sessions it is drawn from, the views named at its end. The view gets
// the room above that line and what the board gets: the sessions the
// board's search reaches (what clicks narrowed it to, said along the pane's
// top: BoardScope) and where review left each, and the search to narrow.
//
// What a view (`view.Body`) is given:
// - `facts`: what the search reaches, counted (`facts.rings`, see
//   lib/ringCounts.js), `stale` while the next read is under way (the frame
//   dims the view meanwhile);
// - `range` and `bounds` ({ from, to } in ms): the page's range;
// - `width` and `height`: the room it has, which it fills and keeps to;
// - `query`, `onPick(clause, keep)`, `onPickAll(clauses, keep)`: the search,
//   and a click that narrows it to a clause (or several), or widens it back
//   out of what it holds already; `keep`, where the view knows it, is drawn
//   at once until the narrower search's own counts are read
//   (`facts.narrowing`): `{ rings }`, the counts in hand narrowed to it;
//   `clauseFor(def, value)`: a board step's clause;
// - `hues`: each agent's colour; `groups`: whether the directory has groups;
// - `onShowAll(part)`: goes down the page to a section (Triage, People,
//   Agents...), for the rest of what the view folds.
import { Suspense, memo, useCallback, useEffect, useMemo, useState } from "react";
import { useLendFinder } from "../BoardScope.jsx";
import ErrorBoundary from "../ErrorBoundary.jsx";
import { FINDS, FINDS_NO_GROUPS, LIT_FILL, SAMPLE_H, SampleLine, useClauseFor } from "../FlowBoard.jsx";
import { withoutClause } from "../SearchPalette.jsx";
import { appendClause, holdsClause, setWorkspaceSearch, useWorkspaceSearch } from "../../lib/workspaceSearch.js";
import { BodyState } from "./parts.jsx";

const READING = { status: "loading", sessions: [] };
// Keyboard focus on anything a view draws, as pointing is: the fill a shade
// up, framed a shade up, so no browser ring.
const VIEW_FOCUS = "[&_button]:outline-none [&_button:focus-visible]:bg-[var(--row-hover)] [&_button:focus-visible]:[outline:1px_solid_color-mix(in_srgb,var(--muted)_55%,transparent)] [&_button:focus-visible]:[outline-offset:-1px]";

// `onPicked`: told of a click that narrowed or widened the search, as
// `onPick` is of each of its own.
function WelcomeFrame({ view, facts, range, bounds, width, height, onPick, onPicked = null, hues, groups = true, onShowAll, aside = null }) {
  const clauseFor = useClauseFor(facts);
  const [query] = useWorkspaceSearch();
  // What the sidebar's search finds among: what this board read.
  useLendFinder({ kinds: groups ? FINDS : FINDS_NO_GROUPS, sessions: facts.sessions, clauseFor });
  // A click that narrows the search draws at once the counts in hand
  // narrowed to it, until the narrower search's own are read: the reads are
  // slow, and the view standing still meanwhile read as the click not
  // taking. `narrowing`: what is drawn meanwhile, and the counts it was made
  // from.
  const [narrowing, setNarrowing] = useState(null);
  const narrowTo = useCallback((narrows, keep) => {
    setNarrowing(narrows && keep ? { keep, from: facts.rings } : null);
  }, [facts.rings]);
  const pick = useCallback((clause, keep = null) => {
    narrowTo(!!clause && !holdsClause(query, clause), keep);
    onPick(clause);
  }, [narrowTo, onPick, query]);
  // A click that stands for several things at once (a cell of a person and
  // a kind of work): the search holding them all already widens back out of
  // them all; else it narrows to those it does not hold.
  const onPickAll = useCallback((clauses, keep = null) => {
    const list = clauses.filter(Boolean);
    narrowTo(list.length > 0 && !list.every((c) => holdsClause(query, c)), keep);
    setWorkspaceSearch((t) => {
      if (!list.length) return t;
      if (list.every((c) => holdsClause(t, c))) return list.reduce((acc, c) => withoutClause(acc, c), t);
      return list.reduce((acc, c) => appendClause(acc, c), t);
    }, { now: true });
    if (list.length) onPicked?.();
  }, [narrowTo, query, onPicked]);
  // The narrower search's own in (or its read failed), it is drawn as read.
  const landed = !!narrowing && !facts.stale && facts.rings !== narrowing.from;
  useEffect(() => { if (landed) setNarrowing(null); }, [landed]);
  const shown = useMemo(() => (narrowing && !landed
    ? { ...facts, ...narrowing.keep, stale: false, narrowing: true }
    : facts), [facts, narrowing, landed]);
  const bodyH = Math.max(240, height - SAMPLE_H);
  const Body = view.Body;
  return (
    <div className="relative" style={{ width, height }} data-welcome-view={view.id}>
      <div className={`absolute left-0 top-0 transition-opacity duration-200 ${VIEW_FOCUS}`} style={{ width, height: bodyH, opacity: shown.stale ? 0.45 : 1, "--row-hover": LIT_FILL }} data-welcome-body="" data-welcome-narrowing={shown.narrowing ? "" : undefined}>
        {/* A view that fails to draw says so in its own room, the page around it kept. */}
        <ErrorBoundary key={view.id} fallback={<p className="m-0 text-[12px] text-[color:var(--muted)]" role="alert">This view could not be drawn.</p>}>
          <Suspense fallback={<BodyState facts={READING} />}>
            <Body facts={shown} range={range} bounds={bounds} width={width} height={bodyH} query={query} onPick={pick} onPickAll={onPickAll} clauseFor={clauseFor} hues={hues} groups={groups} onShowAll={onShowAll} />
          </Suspense>
        </ErrorBoundary>
      </div>
      <SampleLine facts={shown} top={bodyH + 6} width={width} aside={aside} />
    </div>
  );
}

// Drawn again only when what it is handed changes, not each time the page
// around it is (its own room changing as a column's seam is dragged).
export default memo(WelcomeFrame);
